import { isAllowedStripeCheckoutUrl } from '@/utilities/stripe'

export const PLAN_SIGNUP_PARAM = 'plan-signup'
export const PLAN_SIGNUP_JOIN_PATH = '/join'
export const MEMBERSHIP_AUTO_CHECKOUT_PARAM = 'checkout'
export const MEMBERSHIP_AUTO_CHECKOUT_VALUE = '1'
export const MEMBERSHIP_CHECKOUT_SUCCESS_PARAM = 'membership-checkout'
export const MEMBERSHIP_CHECKOUT_SUCCESS_VALUE = 'success'

export function getPlanSignupMembershipLevelId(
  searchParams: Pick<URLSearchParams, 'get'>,
): number | null {
  const planSignupId = searchParams.get(PLAN_SIGNUP_PARAM)
  if (!planSignupId) return null
  const planId = Number(planSignupId)
  return Number.isInteger(planId) && planId > 0 ? planId : null
}

export function joinUrlWithPlan(planId: number | string, extra?: Record<string, string>): string {
  const params = new URLSearchParams({ [PLAN_SIGNUP_PARAM]: String(planId) })
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      params.set(key, value)
    }
  }
  return `${PLAN_SIGNUP_JOIN_PATH}?${params.toString()}`
}

/** Where to send the user after signup so checkout can resume on /join. */
export function joinCheckoutResumeUrl(planId: number | string): string {
  return joinUrlWithPlan(planId, {
    [MEMBERSHIP_AUTO_CHECKOUT_PARAM]: MEMBERSHIP_AUTO_CHECKOUT_VALUE,
  })
}

export function shouldAutoStartMembershipCheckout(
  searchParams: Pick<URLSearchParams, 'get'>,
): number | null {
  if (searchParams.get(MEMBERSHIP_AUTO_CHECKOUT_PARAM) !== MEMBERSHIP_AUTO_CHECKOUT_VALUE) {
    return null
  }
  return getPlanSignupMembershipLevelId(searchParams)
}

export async function startMembershipCheckout({
  membershipLevelId,
  successUrl,
  cancelUrl,
}: {
  membershipLevelId: number
  successUrl: string
  cancelUrl: string
}): Promise<string> {
  const response = await fetch('/api/stripe/handle-membership', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      membershipLevelId,
      successUrl,
      cancelUrl,
    }),
  })

  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.error || 'Failed to start checkout')
  }

  const data = await response.json()
  if (!data.url) {
    throw new Error('Failed to start checkout')
  }

  return data.url
}

export async function redirectToMembershipCheckout(membershipLevelId: number): Promise<void> {
  const origin = window.location.origin
  const successUrl = new URL('/dashboard', origin)
  successUrl.searchParams.set(MEMBERSHIP_CHECKOUT_SUCCESS_PARAM, MEMBERSHIP_CHECKOUT_SUCCESS_VALUE)
  const url = await startMembershipCheckout({
    membershipLevelId,
    successUrl: successUrl.toString(),
    cancelUrl: new URL(joinUrlWithPlan(membershipLevelId), origin).toString(),
  })
  if (typeof url !== 'string' || !isAllowedStripeCheckoutUrl(url)) {
    throw new Error('Invalid checkout URL')
  }
  window.location.assign(url)
}
